import { mdiCalendar, mdiHelp, mdiLightbulbOutline, mdiMessageTextOutline } from '@lumapps/lumx/icons';
import { CodeBlock } from '@lumapps/wrex-code-block/components/blocks/CodeBlock';
import { CODE_BLOCK } from '@lumapps/wrex-code-block/constants';
import { Image } from '@lumapps/wrex-image/components/blocks/Image';
import { Link } from '@lumapps/wrex-link/components/blocks/Link';
import { LINK, LINK_FEATURES } from '@lumapps/wrex-link/constants';
import { ListItem } from '@lumapps/wrex-list/components/blocks/ListItem';
import { UnorderedList } from '@lumapps/wrex-list/components/blocks/UnorderedList';
import { LIST_FEATURES, LIST_ITEM, UNORDERED_LIST } from '@lumapps/wrex-list/constants';
import { Heading } from '@lumapps/wrex-typography/components/blocks/Heading';
import { Bold, Italic } from '@lumapps/wrex-typography/components/blocks/Mark';
import { Paragraph } from '@lumapps/wrex-typography/components/blocks/Paragraph';
import { BOLD, HEADING, ITALIC, PARAGRAPH, TYPOGRAPHY_FEATURES } from '@lumapps/wrex-typography/constants';
import { UserMention } from '@lumapps/wrex-user-mention/components/blocks/UserMention';
import { USER_MENTION } from '@lumapps/wrex-user-mention/constants';
import type { HtmlPasteConfig } from '@lumapps/wrex/utils/getHtmlPasteOptions';

import { POSTS } from './keys';
import { PostType, RefreshEvent } from './types';

export const PACKAGE_PREFIX = 'posts';

/** Maps PostType to mdi icon */
export const POST_TYPE_ICONS: Record<PostType, string> = {
    [PostType.DEFAULT]: mdiMessageTextOutline,
    [PostType.EVENT]: mdiCalendar,
    [PostType.QUESTION]: mdiHelp,
    [PostType.IDEA]: mdiLightbulbOutline,
};

/** Maps PostType to translation keys. */
export const POST_TYPE_LABEL_KEYS: Record<PostType, string> = {
    [PostType.DEFAULT]: POSTS.POST_TYPE,
    [PostType.IDEA]: POSTS.POST_TYPE_IDEA,
    [PostType.EVENT]: POSTS.POST_TYPE_EVENT,
    [PostType.QUESTION]: POSTS.POST_TYPE_QUESTION,
};

/** Maps PostType to translation keys for Spaces */
export const POST_TYPE_SPACE_LABEL_KEYS: Record<PostType, string> = {
    [PostType.DEFAULT]: POSTS.CREATE_A_POST,
    [PostType.IDEA]: POSTS.POST_CREATE_TYPE_IDEA,
    [PostType.EVENT]: POSTS.POST_CREATE_TYPE_EVENT,
    [PostType.QUESTION]: POSTS.POST_CREATE_TYPE_QUESTION,
};

/** Maps PostType to translation keys for post creation. */
export const POST_TYPE_CREATE_KEYS: Record<PostType, string> = {
    [PostType.DEFAULT]: POSTS.NEW_POST,
    [PostType.IDEA]: POSTS.NEW_IDEA,
    [PostType.EVENT]: POSTS.NEW_EVENT,
    [PostType.QUESTION]: POSTS.NEW_QUESTION,
};

/** Maps PostType to translation keys for post edition. */
export const POST_TYPE_EDIT_KEYS: Record<PostType, string> = {
    [PostType.DEFAULT]: POSTS.EDIT_POST,
    [PostType.IDEA]: POSTS.EDIT_IDEA,
    [PostType.EVENT]: POSTS.EDIT_EVENT,
    [PostType.QUESTION]: POSTS.EDIT_QUESTION,
};

/** Maps PostType to tooltip translation keys. */
export const POST_TYPE_LONG_LABEL_KEYS: Record<PostType, string> = {
    [PostType.DEFAULT]: POSTS.CREATE_A_POST,
    [PostType.IDEA]: POSTS.POST_CREATE_TYPE_IDEA,
    [PostType.EVENT]: POSTS.POST_CREATE_TYPE_EVENT,
    [PostType.QUESTION]: POSTS.POST_CREATE_TYPE_QUESTION,
};

export const MAX_TAGZ_NUMBER = 20;

/** API fields fetched before editing a post */
export const POST_EDIT_FIELDS =
    'uid,eventStartDate,eventEndDate,startDate,status,title,content,postType,images,postStatusDetails,files,isPinned,links,authorDetails(uid,customer,firstName,lastName),content,customer,instance,mentions,createdAt,parentContentDetails(postTypes,postStatuses,securedRepository,renderingType),version,id,tags,tagz,postStatus,tagsDetails,externalKey,mentionsDetails(uid,apiProfile(name(fullName,givenName,familyName)))';

export const REFRESH_EVENTS: Record<string, RefreshEvent> = {
    POST_UNPIN: 'unpin',
    POST_PIN: 'pin',
    POST_DELETE: 'delete',
    POST_MOVE: 'move',
    POST_SHARE: 'share',
    POST_UNSHARE: 'unshare',
    COMMENT_ADD: 'add',
    COMMENT_HIDE: 'hide',
    COMMENT_MARK_RELEVANT: 'mark',
    COMMENT_UNMARK_RELEVANT: 'unmark',
};

const STARTING_BREAKPOINT = 270;
const BUTTON_SIZE = 36;
const DIVIDER_SIZE = 9; // 1px + 2x4px margin
/**
 * Defines the post editor toolbar breakpoints according to a button rounded width.
 */
export const POST_EDITOR_TOOLBAR_BREAKPOINTS = {
    0: 0,
    1: STARTING_BREAKPOINT + BUTTON_SIZE + DIVIDER_SIZE,
    2: STARTING_BREAKPOINT + BUTTON_SIZE * 2 + DIVIDER_SIZE,
    3: STARTING_BREAKPOINT + BUTTON_SIZE * 3 + DIVIDER_SIZE,
    4: STARTING_BREAKPOINT + BUTTON_SIZE * 4 + DIVIDER_SIZE * 2,
    5: STARTING_BREAKPOINT + BUTTON_SIZE * 5 + DIVIDER_SIZE * 3,
    6: STARTING_BREAKPOINT + BUTTON_SIZE * 6 + DIVIDER_SIZE * 3,
    7: STARTING_BREAKPOINT + BUTTON_SIZE * 7 + DIVIDER_SIZE * 4,
    8: STARTING_BREAKPOINT + BUTTON_SIZE * 8 + DIVIDER_SIZE * 4,
};

/**
 * Defines the post editor toolbar number of buttons to display.
 */
export const POST_EDITOR_TOOLBAR_BUTTONS: Record<keyof typeof POST_EDITOR_TOOLBAR_BREAKPOINTS, number> = {
    0: 0,
    1: 3, // Nothing but the undo/redo + block type selector + divider
    2: 4, // Bold
    3: 5, // Italic
    4: 7, // Link plugin + divider
    5: 9, // List + divider
    6: 10, // emoji plugin
    7: 12, // Image plugin + divider
    8: 13, // Video attachment
};

export const DISABLE_QUICK_POST_FF = 'disable-quick-post';

export const MAX_SYMBOLS_IN_POST = 1300;

/** List of the plugins enabled features for the post editor */
export const postEditorEnabledFeatures = {
    typography: [TYPOGRAPHY_FEATURES.bold, TYPOGRAPHY_FEATURES.italic, TYPOGRAPHY_FEATURES.heading],
    list: [LIST_FEATURES.unorderedList],
    link: [LINK_FEATURES.linkPreviewConversion],
};

/** List of post Wrex elements */
export const POST_WREX_ELEMENTS = [Paragraph, Heading, Link, Image, UnorderedList, ListItem, UserMention, CodeBlock];

/** List of post Wrex marks */
export const POST_WREX_MARKS = [Bold, Italic];

/** Allowed elements to be pasted from HTML to slate, in a post editor */
export const POST_HTML_PASTE_OPTIONS = {
    elements: [HEADING, PARAGRAPH, CODE_BLOCK, UNORDERED_LIST, LIST_ITEM, LINK, USER_MENTION],
    marks: [BOLD, ITALIC],
    maxHeadingLevel: 2,
} satisfies HtmlPasteConfig;
